import { LANGUAGE_LABELS } from '../constant';

export type LocaleOption = {
  locale: string | null;
  label: string;
};

export function getLanguageLabel(locale: string): string {
  if (LANGUAGE_LABELS[locale]) {
    return LANGUAGE_LABELS[locale];
  }
  const base = locale.split('-')[0];
  if (LANGUAGE_LABELS[base]) {
    return LANGUAGE_LABELS[base];
  }
  return locale;
}

const RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur']);

export function isRtlLocale(locale: string | null): boolean {
  if (!locale) return false;
  const base = locale.split('-')[0];
  return RTL_LANGUAGES.has(base);
}

export function buildLocaleOptions(supportedLocales: string[], canonicalLabel = 'Original'): LocaleOption[] {
  if (supportedLocales.length === 0) {
    return [];
  }
  const canonical: LocaleOption = { locale: null, label: canonicalLabel };
  const translated = supportedLocales.map((locale) => ({
    locale,
    label: getLanguageLabel(locale),
  }));
  return [canonical, ...translated];
}

const LANGUAGE_TO_COUNTRY: Record<string, string> = {
  en: 'US',
  es: 'ES',
  fr: 'FR',
  de: 'DE',
  it: 'IT',
  pt: 'BR',
  ja: 'JP',
  ko: 'KR',
  zh: 'CN',
  ar: 'SA',
  hi: 'IN',
  th: 'TH',
  vi: 'VN',
  id: 'ID',
  ms: 'MY',
  fil: 'PH',
  nl: 'NL',
  sv: 'SE',
  da: 'DK',
  fi: 'FI',
  no: 'NO',
  pl: 'PL',
  ru: 'RU',
  tr: 'TR',
  uk: 'UA',
  cs: 'CZ',
  ro: 'RO',
  hu: 'HU',
  el: 'GR',
  he: 'IL',
  fa: 'IR',
  ur: 'PK',
  bn: 'BD',
  ta: 'IN',
  te: 'IN',
  mr: 'IN',
  gu: 'IN',
  kn: 'IN',
  ml: 'IN',
  my: 'MM',
  km: 'KH',
  lo: 'LA',
  si: 'LK',
  ne: 'NP',
  am: 'ET',
  sw: 'KE',
  af: 'ZA',
};

export function getCountryFlag(locale: string): string | null {
  const parts = locale.split('-');
  let countryCode = parts.length > 1 ? parts[parts.length - 1].toUpperCase() : null;

  if (!countryCode || countryCode.length !== 2) {
    countryCode = LANGUAGE_TO_COUNTRY[parts[0]] ?? null;
  }

  if (!countryCode) return null;

  return String.fromCodePoint(...countryCode.split('').map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

export function buildLocaleChangeUrl(currentHref: string, locale: string | null): string {
  const url = new URL(currentHref);
  if (locale) {
    url.searchParams.set('locale', locale);
  } else {
    url.searchParams.delete('locale');
  }
  return url.toString();
}

/**
 * The API translates option labels but not condition values in showOptions.
 * This causes conditional questions to break in non-default languages because
 * the rules engine compares the translated selected value (e.g. "Sí") against
 * the untranslated condition value (e.g. "Yes").
 *
 * This function patches condition values to use the translated option text
 * by mapping original→translated via array index.
 */
export function translateConditionValues(translatedPages: any[], originalPages: any[]): any[] {
  const originalOptionsMap: Record<string, string[]> = {};
  const translatedOptionsMap: Record<string, string[]> = {};

  for (const page of originalPages) {
    for (const question of page.questions || []) {
      const optionsField = question.value?.find((f: any) => f.fieldKey === 'options');
      if (
        optionsField?.fieldValue &&
        Array.isArray(optionsField.fieldValue) &&
        optionsField.fieldValue.every((v: any) => typeof v === 'string')
      ) {
        originalOptionsMap[question.originQuestionId] = optionsField.fieldValue;
      }
    }
  }

  for (const page of translatedPages) {
    for (const question of page.questions || []) {
      const optionsField = question.value?.find((f: any) => f.fieldKey === 'options');
      if (
        optionsField?.fieldValue &&
        Array.isArray(optionsField.fieldValue) &&
        optionsField.fieldValue.every((v: any) => typeof v === 'string')
      ) {
        translatedOptionsMap[question.originQuestionId] = optionsField.fieldValue;
      }
    }
  }

  const translateValue = (value: any, original: string[], translated: string[]): any => {
    if (typeof value === 'string') {
      const idx = original.indexOf(value);
      return idx >= 0 && idx < translated.length ? translated[idx] : value;
    }
    if (Array.isArray(value)) {
      return value.map((v) => {
        if (typeof v === 'string') {
          const idx = original.indexOf(v);
          return idx >= 0 && idx < translated.length ? translated[idx] : v;
        }
        return v;
      });
    }
    return value;
  };

  return translatedPages.map((page) => ({
    ...page,
    questions: (page.questions || []).map((question: any) => {
      if (!question.showOptions?.conditions) return question;

      return {
        ...question,
        showOptions: {
          ...question.showOptions,
          conditions: question.showOptions.conditions.map((condition: any) => ({
            ...condition,
            logic: (condition.logic || []).map((logicItem: any) => {
              const original = originalOptionsMap[logicItem.questionId];
              const translated = translatedOptionsMap[logicItem.questionId];
              if (!original || !translated || original.length !== translated.length) return logicItem;

              return { ...logicItem, value: translateValue(logicItem.value, original, translated) };
            }),
          })),
        },
      };
    }),
  }));
}

/** Per page-question id: the translated option label the guest saw → its canonical value. */
export type CanonicalOptionMap = Record<string, Record<string, string>>;

const stringOptionsOf = (question: any): string[] | null => {
  const options = question.value?.find((f: any) => f.fieldKey === 'options')?.fieldValue;
  return Array.isArray(options) && options.every((v: any) => typeof v === 'string') ? options : null;
};

/**
 * Flat string options (dropdown / multiple-choice) have no canonical `value` beside the label, so
 * submissions must map back by index; analytics match answers on canonical strings (GEN-24704).
 */
export function buildCanonicalOptionMap(translatedPages: any[], originalPages: any[]): CanonicalOptionMap {
  const canonicalByOrigin: Record<string, string[]> = {};
  for (const page of originalPages) {
    for (const question of page.questions || []) {
      const options = stringOptionsOf(question);
      if (options) canonicalByOrigin[question.originQuestionId] = options;
    }
  }

  const result: CanonicalOptionMap = {};
  for (const page of translatedPages) {
    for (const question of page.questions || []) {
      const translated = stringOptionsOf(question);
      const canonical = canonicalByOrigin[question.originQuestionId];
      if (!translated || !canonical || translated.length !== canonical.length) continue;
      result[question.id] = Object.fromEntries(translated.map((label, i) => [label, canonical[i]]));
    }
  }
  return result;
}

export function toCanonicalAnswer(value: any, labelToCanonical?: Record<string, string>): any {
  if (!labelToCanonical) return value;
  const toCanonical = (v: any) =>
    typeof v === 'string' && Object.prototype.hasOwnProperty.call(labelToCanonical, v) ? labelToCanonical[v] : v;
  return Array.isArray(value) ? value.map(toCanonical) : toCanonical(value);
}
